import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase config MISSING:', {
    url: supabaseUrl ? 'Set' : 'MISSING',
    key: supabaseAnonKey ? 'Set' : 'MISSING'
  });
}

/**
 * Retry-on-abort fetch wrapper.
 * 
 * In development, React StrictMode double-mounts components which can cause
 * the Supabase auth SDK's internal lock to abort in-flight fetch requests.
 * Browser extensions can also interfere with fetch requests.
 * 
 * This wrapper catches AbortErrors and retries the request transparently.
 * It does NOT add its own AbortController/signal — it only catches aborts
 * from external sources and retries.
 */
const retryFetch = (url, options) => {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 250; // ms

  const attempt = (n) =>
    fetch(url, n > 0 ? { ...options, signal: undefined } : options).catch((err) => {
      if (
        (err.name === 'AbortError' || err.message?.includes('signal is aborted')) &&
        n < MAX_RETRIES
      ) {
        console.warn(`Fetch aborted (attempt ${n + 1}), retrying...`);
        return new Promise((r) => setTimeout(r, RETRY_DELAY * (n + 1))).then(() =>
          attempt(n + 1)
        );
      }
      throw err;
    });

  return attempt(0);
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Bypass the Navigator Locks API. Supabase auth-js uses browser locks
    // (navigator.locks.request) for multi-tab synchronization, but in dev
    // the lock frequently aborts due to HMR re-evaluating the module and
    // creating competing client instances. A no-op lock is safe for
    // single-tab usage and eliminates the AbortError entirely.
    lock: async (_name, _acquireTimeout, fn) => await fn(),
  },
  global: {
    headers: {
      'x-application-name': 'kagi-app'
    },
    fetch: retryFetch
  },
  db: {
    schema: 'public'
  }
});

export default supabase;
